import { useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import type { BoardBlock, BoardSummary, Organization } from '../lib/api';
import { Popover } from '../ui/controls';
import { Icon } from '../ui/icons';
import { BoardAccessPanel } from './BoardAccessPanel';
import { formatRelative } from './dates';

export interface BoardsViewProps {
  org: Organization;
  orgs: Organization[];
  boards: BoardSummary[];
  blocks: BoardBlock[];
  loading: boolean;
  onCreateBoard: (name: string, blockId?: string | null) => Promise<void>;
  onOpenBoard: (id: string) => void;
  onRenameBoard: (id: string, name: string) => Promise<void>;
  onMoveBoard: (id: string, orgId: string) => Promise<void>;
  onSetBoardBlock: (id: string, blockId: string | null) => Promise<void>;
  onDeleteBoard: (id: string) => Promise<void>;
  onCreateBlock: (name: string) => Promise<void>;
  onRenameBlock: (id: string, name: string) => Promise<void>;
  onDeleteBlock: (id: string) => Promise<void>;
  /** Blocks in their new top-to-bottom order. */
  onReorderBlocks: (orderedIds: string[]) => Promise<void>;
}

/** What is currently under the pointer, mid-drag. */
type Dragged = { kind: 'board'; id: string } | { kind: 'block'; id: string } | null;

/*
 * Drag types are read off `dataTransfer.types` during `dragover`, because the
 * payload itself is unreadable until the drop. That is what lets a section
 * accept a board but ignore a block being reordered past it.
 */
const BOARD_MIME = 'application/x-kanvas-board';
const BLOCK_MIME = 'application/x-kanvas-block';

export function BoardsView({
  org,
  orgs,
  boards,
  blocks,
  loading,
  onCreateBoard,
  onOpenBoard,
  onRenameBoard,
  onMoveBoard,
  onSetBoardBlock,
  onDeleteBoard,
  onCreateBlock,
  onRenameBlock,
  onDeleteBlock,
  onReorderBlocks,
}: BoardsViewProps) {
  const { user } = useAuth();
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renamingBlockId, setRenamingBlockId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [dragged, setDragged] = useState<Dragged>(null);
  /** Where a dragged board would land: a block id, or `'none'` for «вне блоков». */
  const [dropSection, setDropSection] = useState<string | null>(null);
  /** Block a dragged block would be inserted before. */
  const [dropBlock, setDropBlock] = useState<string | null>(null);
  /** The block being browsed, or `null` at the top level. */
  const [openBlockId, setOpenBlockId] = useState<string | null>(null);

  const endDrag = () => {
    setDragged(null);
    setDropSection(null);
    setDropBlock(null);
  };

  /** Moves the dragged block to where `targetId` currently sits. */
  const reorderTo = async (targetId: string) => {
    if (!dragged || dragged.kind !== 'block' || dragged.id === targetId) return;
    const ids = blocks.map((b) => b.id).filter((id) => id !== dragged.id);
    const at = ids.indexOf(targetId);
    ids.splice(at < 0 ? ids.length : at, 0, dragged.id);
    await onReorderBlocks(ids);
  };

  /** Up/down buttons: the pointer-and-keyboard equivalent of dragging a block. */
  const nudgeBlock = async (blockId: string, delta: -1 | 1) => {
    const ids = blocks.map((b) => b.id);
    const from = ids.indexOf(blockId);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= ids.length) return;
    ids.splice(to, 0, ...ids.splice(from, 1));
    await onReorderBlocks(ids);
  };

  // A block deleted underneath us (or by someone else) drops the view back to
  // the top level rather than showing an empty folder that no longer exists.
  const openBlock = blocks.find((b) => b.id === openBlockId) ?? null;

  const isOrgAdmin = org.myRole === 'admin' || user?.isSuperAdmin === true;
  const isStudent = org.myKind === 'student';
  const ownBoards = user ? boards.filter((b) => b.createdBy === user.id).length : 0;
  /*
   * Mirrors the server's rule so the button is simply absent rather than
   * failing on click: the owner and teachers create freely, a student gets
   * one board for homework, and everyone else works on what they are given.
   */
  const canCreate = isOrgAdmin || org.myKind === 'teacher' || (isStudent && ownBoards === 0);

  const create = async () => {
    setCreating(true);
    try {
      // Filing a board into a block is an owner's call, so for everyone else
      // the new board lands at the top level even if they created it inside one.
      const blockId = isOrgAdmin ? (openBlock?.id ?? null) : null;
      await onCreateBoard(isStudent ? 'Домашняя работа' : 'Новая доска', blockId);
    } finally {
      setCreating(false);
    }
  };

  const draggingBoard = dragged?.kind === 'board';
  /** A board is in a block only if that block still exists. */
  const blockOf = (board: BoardSummary) =>
    board.blockId && blocks.some((b) => b.id === board.blockId) ? board.blockId : null;
  const boardsInBlock = (blockId: string | null) =>
    boards.filter((board) => blockOf(board) === blockId);
  const visibleBoards = boardsInBlock(openBlock?.id ?? null);

  /*
   * What an empty grid says. The big «Здесь пока нет досок» panel covers the
   * genuinely empty organisation; these are the narrower cases, where the
   * boards are simply somewhere else.
   */
  const emptyHint = openBlock
    ? isOrgAdmin
      ? 'Блок пуст — перетащите сюда доску из общего списка или создайте новую.'
      : 'В этом блоке пока нет досок.'
    : boards.length > 0
      ? 'Все доски разложены по блокам — откройте любой из них.'
      : blocks.length > 0
        ? 'Досок пока нет. Создайте первую — или сначала откройте блок.'
        : null;

  return (
    <section className="dash-content">
      <header className="dash-header">
        <div className="dash-crumbs">
          {openBlock && (
            /*
             * The way back out, and also where a board goes to leave the
             * block — dropping one here is the reverse of dropping it in.
             */
            <button
              type="button"
              className={`crumb-up ${dropSection === 'none' ? 'is-drop-target' : ''}`}
              onClick={() => setOpenBlockId(null)}
              onDragOver={(e) => {
                if (!isOrgAdmin || !e.dataTransfer.types.includes(BOARD_MIME)) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                setDropSection('none');
              }}
              onDragLeave={() => setDropSection(null)}
              onDrop={(e) => {
                if (!isOrgAdmin || !e.dataTransfer.types.includes(BOARD_MIME)) return;
                e.preventDefault();
                const boardId = e.dataTransfer.getData(BOARD_MIME) || dragged?.id;
                endDrag();
                if (boardId) void onSetBoardBlock(boardId, null);
              }}
            >
              <Icon name="arrowLeft" size={15} />
              {org.name}
            </button>
          )}

          {openBlock && renamingBlockId === openBlock.id ? (
            <input
              className="dash-inline-input is-title"
              defaultValue={openBlock.name}
              autoFocus
              maxLength={60}
              onBlur={async (e) => {
                const name = e.target.value.trim();
                const blockId = renamingBlockId;
                setRenamingBlockId(null);
                if (blockId && name && name !== openBlock.name) await onRenameBlock(blockId, name);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.currentTarget.blur();
                if (e.key === 'Escape') {
                  e.currentTarget.value = openBlock.name;
                  e.currentTarget.blur();
                }
              }}
            />
          ) : openBlock && isOrgAdmin ? (
            // Clicking the title is the rename: inside the block the name is
            // the heading, so that is the thing people reach for.
            <h1>
              <button
                type="button"
                className="board-block-rename"
                title="Нажмите, чтобы переименовать"
                onClick={() => setRenamingBlockId(openBlock.id)}
              >
                {openBlock.name}
              </button>
            </h1>
          ) : (
            <h1>{openBlock ? openBlock.name : org.name}</h1>
          )}

          <p className="dash-subtitle">
            {loading
              ? 'Загрузка…'
              : openBlock
                ? `${visibleBoards.length} ${plural(visibleBoards.length, 'доска', 'доски', 'досок')} в блоке`
                : `${boards.length} ${plural(boards.length, 'доска', 'доски', 'досок')}`}
          </p>
        </div>

        <div className="dash-header-actions">
          {openBlock && isOrgAdmin && (
            <button
              type="button"
              className="ghost-btn is-danger"
              // No need to navigate out: once the block is gone the view has
              // nothing to be inside of and falls back to the top level.
              onClick={() => void onDeleteBlock(openBlock.id)}
            >
              <Icon name="trash" size={16} />
              Удалить блок
            </button>
          )}
          {!openBlock && isOrgAdmin && (
            <button
              type="button"
              className="ghost-btn"
              onClick={() => void onCreateBlock('Новый блок')}
            >
              <Icon name="plus" size={16} />
              Блок
            </button>
          )}
          {canCreate && (
            <button type="button" className="primary-btn" disabled={creating} onClick={create}>
              <Icon name="plus" size={16} />
              {isStudent ? 'Создать свою доску' : 'Создать доску'}
            </button>
          )}
        </div>
      </header>

      {!loading && boards.length === 0 && blocks.length === 0 && (
        <div className="dash-empty">
          <Icon name="boards" size={28} />
          <h2>Здесь пока нет досок</h2>
          <p>
            {canCreate
              ? isStudent
                ? 'Создайте свою доску для домашней работы — удалить её сможет только владелец организации.'
                : 'Создайте первую и пригласите на неё нужных людей.'
              : 'Доски появятся здесь, когда вас на них пригласят.'}
          </p>
          {canCreate && (
            <button type="button" className="primary-btn" onClick={create}>
              <Icon name="plus" size={16} />
              Создать доску
            </button>
          )}
        </div>
      )}

      {/*
        Top level: the blocks as folders you go into. Boards filed inside one
        are not repeated here, so a long list stays put away.
      */}
      {!openBlock && blocks.length > 0 && (
        <div className="block-grid">
          {blocks.map((block, index) => {
            const count = boardsInBlock(block.id).length;
            // Students never need an empty folder — nothing inside is for them.
            if (isStudent && count === 0) return null;
            const acceptsBoard = isOrgAdmin && draggingBoard;

            return (
              <div
                key={block.id}
                className={[
                  'block-card',
                  dropSection === block.id && 'is-drop-target',
                  dropBlock === block.id && 'is-drop-before',
                  dragged?.kind === 'block' && dragged.id === block.id && 'is-dragging',
                ]
                  .filter(Boolean)
                  .join(' ')}
                draggable={isOrgAdmin && renamingBlockId !== block.id}
                onDragStart={(e) => {
                  e.dataTransfer.setData(BLOCK_MIME, block.id);
                  e.dataTransfer.effectAllowed = 'move';
                  setDragged({ kind: 'block', id: block.id });
                }}
                onDragEnd={endDrag}
                onDragOver={(e) => {
                  // One card takes two kinds of drop: a board goes in, a block
                  // takes this card's place in the order.
                  if (acceptsBoard && e.dataTransfer.types.includes(BOARD_MIME)) {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    setDropSection(block.id);
                    return;
                  }
                  if (!e.dataTransfer.types.includes(BLOCK_MIME)) return;
                  if (dragged?.kind === 'block' && dragged.id === block.id) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  setDropBlock(block.id);
                }}
                onDragLeave={(e) => {
                  if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
                  setDropSection((c) => (c === block.id ? null : c));
                  setDropBlock((c) => (c === block.id ? null : c));
                }}
                onDrop={async (e) => {
                  e.preventDefault();
                  if (e.dataTransfer.types.includes(BOARD_MIME)) {
                    const boardId = e.dataTransfer.getData(BOARD_MIME) || dragged?.id;
                    endDrag();
                    const board = boards.find((b) => b.id === boardId);
                    if (!board || blockOf(board) === block.id) return;
                    void onSetBoardBlock(board.id, block.id);
                    return;
                  }
                  setDropBlock(null);
                  await reorderTo(block.id);
                  endDrag();
                }}
              >
                {renamingBlockId === block.id ? (
                  <input
                    className="dash-inline-input"
                    defaultValue={block.name}
                    autoFocus
                    maxLength={60}
                    onBlur={async (e) => {
                      const name = e.target.value.trim();
                      setRenamingBlockId(null);
                      if (name && name !== block.name) await onRenameBlock(block.id, name);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') e.currentTarget.blur();
                      if (e.key === 'Escape') {
                        e.currentTarget.value = block.name;
                        e.currentTarget.blur();
                      }
                    }}
                  />
                ) : (
                  <button
                    type="button"
                    className="block-card-open"
                    onClick={() => setOpenBlockId(block.id)}
                  >
                    <Icon name="folder" size={22} />
                    <span className="block-card-name">{block.name}</span>
                    <span className="block-card-count">
                      {count} {plural(count, 'доска', 'доски', 'досок')}
                    </span>
                  </button>
                )}

                {isOrgAdmin && renamingBlockId !== block.id && (
                  <span className="block-card-actions">
                    <button
                      type="button"
                      className="icon-btn"
                      aria-label="Переименовать блок"
                      data-tip="Переименовать"
                      onClick={() => setRenamingBlockId(block.id)}
                    >
                      <Icon name="pencil" size={13} />
                    </button>
                    {/* Dragging is not the only way to reorder: these do the
                        same thing for touch and keyboard. */}
                    <button
                      type="button"
                      className="icon-btn"
                      aria-label="Переместить блок левее"
                      data-tip="Левее"
                      disabled={index === 0}
                      onClick={() => void nudgeBlock(block.id, -1)}
                    >
                      <Icon name="chevronDown" size={13} className="rotate-90" />
                    </button>
                    <button
                      type="button"
                      className="icon-btn"
                      aria-label="Переместить блок правее"
                      data-tip="Правее"
                      disabled={index >= blocks.length - 1}
                      onClick={() => void nudgeBlock(block.id, 1)}
                    >
                      <Icon name="chevronDown" size={13} className="rotate-270" />
                    </button>
                    <button
                      type="button"
                      className="icon-btn"
                      aria-label="Удалить блок"
                      data-tip="Удалить блок"
                      onClick={() => void onDeleteBlock(block.id)}
                    >
                      <Icon name="trash" size={13} />
                    </button>
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="board-grid">
        {visibleBoards.map((board) => (
          <article
            key={board.id}
            className={`board-card ${
              dragged?.kind === 'board' && dragged.id === board.id ? 'is-dragging' : ''
            }`}
            // Only someone allowed to re-file boards can pick one up, so
            // a student never starts a drag that could only be refused.
            draggable={isOrgAdmin && board.canMove && renamingId !== board.id}
            onDragStart={(e) => {
              e.dataTransfer.setData(BOARD_MIME, board.id);
              e.dataTransfer.effectAllowed = 'move';
              setDragged({ kind: 'board', id: board.id });
            }}
            onDragEnd={endDrag}
          >
            {/*
              The whole tile opens the board, so the click target is the card
              rather than a small link — but the actions sit above it in the
              stacking order so they are not swallowed by it.
            */}
            <button
              type="button"
              className="board-card-open"
              aria-label={`Открыть доску ${board.name}`}
              onClick={() => onOpenBoard(board.id)}
            >
              <span className="board-thumb">
                {board.thumbnail ? (
                  <img src={board.thumbnail} alt="" />
                ) : (
                  <Icon name="boards" size={22} />
                )}
              </span>
            </button>

            <div className="board-card-body">
              {renamingId === board.id ? (
                <input
                  className="dash-inline-input"
                  defaultValue={board.name}
                  autoFocus
                  maxLength={120}
                  onBlur={async (e) => {
                    const name = e.target.value.trim();
                    setRenamingId(null);
                    if (name && name !== board.name) await onRenameBoard(board.id, name);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.currentTarget.blur();
                    if (e.key === 'Escape') {
                      e.currentTarget.value = board.name;
                      e.currentTarget.blur();
                    }
                  }}
                />
              ) : board.canRename ? (
                <h3 className="board-card-name">
                  <button
                    type="button"
                    className="board-block-rename"
                    title="Нажмите, чтобы переименовать"
                    onClick={() => setRenamingId(board.id)}
                  >
                    {board.name}
                  </button>
                </h3>
              ) : (
                <h3 className="board-card-name" title={board.name}>
                  {board.name}
                </h3>
              )}

              <p className="board-card-meta">
                Изменена {formatRelative(new Date(board.updatedAt).toISOString())}
                {board.myRole && board.myRole !== 'editor' && (
                  <span className="board-role-tag">
                    {board.myRole === 'viewer' ? 'просмотр' : 'комментарии'}
                  </span>
                )}
                {(board.participants ?? 0) > 0 && (
                  <span className="board-live">
                    <span className="board-live-dot" />
                    {board.participants} онлайн
                  </span>
                )}
              </p>
            </div>

            <div className="board-card-actions">
              {(board.canManageMembers || board.canMove || board.createdBy === user?.id) && (
                <BoardAccessPanel boardId={board.id} boardName={board.name} orgId={board.orgId} />
              )}

              <Popover
                align="right"
                title="Действия"
                trigger={() => <Icon name="more" size={16} />}
              >
                {(close) => (
                  <div className="popover-stack">
                    {board.canRename && (
                      <button
                        type="button"
                        className="menu-item"
                        onClick={() => {
                          close();
                          setRenamingId(board.id);
                        }}
                      >
                        <Icon name="pencil" size={16} /> Переименовать
                      </button>
                    )}

                    {board.canMove && blocks.length > 0 && (
                      <>
                        <div className="popover-label">Переместить в блок</div>
                        {blocks.map((block) => (
                          <button
                            key={block.id}
                            type="button"
                            className="menu-item"
                            disabled={board.blockId === block.id}
                            onClick={async () => {
                              close();
                              await onSetBoardBlock(board.id, block.id);
                            }}
                          >
                            <Icon
                              name={board.blockId === block.id ? 'check' : 'folder'}
                              size={16}
                            />
                            {block.name}
                          </button>
                        ))}
                        {board.blockId && (
                          <button
                            type="button"
                            className="menu-item"
                            onClick={async () => {
                              close();
                              await onSetBoardBlock(board.id, null);
                            }}
                          >
                            <Icon name="close" size={16} /> Без блока
                          </button>
                        )}
                      </>
                    )}

                    {board.canMove && orgs.length > 1 && (
                      <>
                        <div className="popover-label">Переместить в организацию</div>
                        {orgs
                          .filter((candidate) => candidate.id !== board.orgId)
                          .map((candidate) => (
                            <button
                              key={candidate.id}
                              type="button"
                              className="menu-item"
                              onClick={async () => {
                                close();
                                await onMoveBoard(board.id, candidate.id);
                              }}
                            >
                              <span
                                className="dash-org-tile is-small"
                                style={{ background: candidate.color }}
                              >
                                {candidate.name.slice(0, 1).toUpperCase()}
                              </span>
                              {candidate.name}
                            </button>
                          ))}
                      </>
                    )}

                    {board.canDelete && (
                      <>
                        <div className="panel-divider" />
                        <button
                          type="button"
                          className="menu-item is-danger"
                          onClick={async () => {
                            close();
                            await onDeleteBoard(board.id);
                          }}
                        >
                          <Icon name="trash" size={16} /> Удалить доску
                        </button>
                      </>
                    )}

                    {!board.canRename && !board.canMove && !board.canDelete && (
                      <div className="org-members-hint">
                        Управлять этой доской может только владелец.
                      </div>
                    )}
                  </div>
                )}
              </Popover>
            </div>
          </article>
        ))}

        {visibleBoards.length === 0 && emptyHint && (
          <p className="board-block-empty">{emptyHint}</p>
        )}
      </div>
    </section>
  );
}

/** Russian needs three plural forms; picking the wrong one reads as a bug. */
export function plural(count: number, one: string, few: string, many: string): string {
  const mod100 = count % 100;
  if (mod100 >= 11 && mod100 <= 14) return many;
  const mod10 = count % 10;
  if (mod10 === 1) return one;
  if (mod10 >= 2 && mod10 <= 4) return few;
  return many;
}
